import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../App';
import { toast } from 'react-hot-toast';
import Select from 'react-select';
import {
  FileText, CheckCircle, Clock, Search, AlertCircle, Plus, Trash2, ArrowLeft, ExternalLink, RefreshCw, Eye, X,
  Building2, Copy, RotateCcw
} from 'lucide-react';
import { useIndiceSociedades, TIPOS_CONTRATO } from '../../hooks/useIndiceSociedades.js';

const BADGE_CONTRATOS = {
  'Constitución': { bg: '#ecfdf5', text: '#065f46', border: '#a7f3d0' },
  'Modificación': { bg: '#eff6ff', text: '#1e40af', border: '#bfdbfe' },
  'Transformación': { bg: '#f5f3ff', text: '#5b21b6', border: '#ddd6fe' },
  'Disolución': { bg: '#fff1f2', text: '#9f1239', border: '#fecdd3' },
};

const CODIGOS_COMERCIO = ['CS', 'CSO', 'MS', 'ME', 'TSO', 'MTR', 'DS', 'DSO', 'DYL', 'IPD', 'ASL', 'ASD', 'MAN', 'MAG'];

const obtenerBadgeContrato = (item) => {
  const cat = item?.tipo_de_contrato_categoria;
  if (cat && BADGE_CONTRATOS[cat]) {
    return { ...BADGE_CONTRATOS[cat], label: item.tipo_de_contrato || cat };
  }
  const tipo = (item?.tipo_de_contrato || '').toUpperCase();
  if (tipo.includes('CONSTITUCION')) return { ...BADGE_CONTRATOS['Constitución'], label: item.tipo_de_contrato };
  if (tipo.includes('MODIF') || tipo.includes('ESTATUTO')) return { ...BADGE_CONTRATOS['Modificación'], label: item.tipo_de_contrato };
  if (tipo.includes('TRANSFORM')) return { ...BADGE_CONTRATOS['Transformación'], label: item.tipo_de_contrato };
  if (tipo.includes('DISOLUC') || tipo.includes('LIQUID')) return { ...BADGE_CONTRATOS['Disolución'], label: item.tipo_de_contrato };
  return { bg: '#f1f5f9', text: '#334155', border: '#cbd5e1', label: item?.tipo_de_contrato || 'Sin especificar' };
};

const BOOKS_MAP = {
  propiedad: 'Registro de Propiedad',
  hipotecas: 'Hipotecas y Gravámenes',
  prohibiciones: 'Prohibiciones e Interdicciones',
  comercio: 'Registro de Comercio',
  aguas: 'Registro de Aguas',
  hipotecas_aguas: 'Hipotecas de Aguas',
  prohibiciones_aguas: 'Prohibiciones de Aguas',
  minas: 'Registro de Minas'
};

// Restringido según spec: solo estas 4 comunas son válidas para el índice.
const COMUNAS = ['San Nicolás', 'San Fabián', 'Ñiquén', 'San Carlos'];

// Datos legacy usan nomenclatura vieja (dueño/vendedor); el select solo ofrece la nueva.
const ROL_LEGACY_MAP = { 'dueño': 'ADQUIRENTE', 'dueno': 'ADQUIRENTE', 'vendedor': 'ENAJENANTE' };
const normalizarRol = (rol) => {
  const limpio = (rol || '').trim().toLowerCase();
  return ROL_LEGACY_MAP[limpio] || (rol || '').toUpperCase();
};

const TRES_DIAS_MS = 3 * 24 * 60 * 60 * 1000;

const IngresoIndice = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const paramRegistroIndiceId = searchParams.get('registro_indice_id');

  // Modo: completar una foja ya reservada por el Tomador de Fojas, buscar y completar
  // Modo: completar una foja ya reservada por el Tomador de Fojas, buscar y completar
  // un índice histórico importado del sistema anterior (nunca reservado digitalmente),
  // ingresar uno completamente nuevo desde cero (desfase histórico, spec 2.4),
  // o consultar el historial registral de Sociedades (Comercio).
  const [modo, setModo] = useState('reserva'); // 'reserva' | 'legacy' | 'nuevo' | 'sociedades'
  const [historico, setHistorico] = useState({
    tipo_registro: 'propiedad', foja_inicio: '', foja_termino: '', numero: '', anio: '', fecha_inscripcion: ''
  });
  // Solo avanza al formulario completo cuando el usuario confirma con el botón "Continuar",
  // no apenas termina de tipear el año (evitaba que la pantalla saltara sola).
  const [historicoConfirmado, setHistoricoConfirmado] = useState(false);

  // Hook del módulo de consulta de sociedades
  const {
    nombreBusqueda: busquedaSociedad,
    setNombreBusqueda: setBusquedaSociedad,
    tipoContratoFiltro: filtroContratoSociedad,
    setTipoContratoFiltro: setFiltroContratoSociedad,
    inscripciones: inscripcionesSociedad,
    cargando: cargandoSociedad,
    haBuscado: haBuscadoSociedad,
    conteoPorTipo: conteoSociedadPorTipo,
    consultarSociedad,
    limpiar: limpiarSociedad,
  } = useIndiceSociedades();
  const [modalSociedades, setModalSociedades] = useState(false);

  // Estado Reservas
  const [pendientes, setPendientes] = useState([]);
  const [loadingPendientes, setLoadingPendientes] = useState(false);
  const [reservaSeleccionada, setReservaSeleccionada] = useState(null);

  // Modal de detalle: ver todos los datos ya ingresados de un índice sin salir del buscador
  const [detalleItem, setDetalleItem] = useState(null);

  // Filtros de la lista de fojas pendientes de completar
  const [filtroSinIntervinientes, setFiltroSinIntervinientes] = useState(false);
  const [filtroSinNaturaleza, setFiltroSinNaturaleza] = useState(false);
  const [filtroSinDescripcion, setFiltroSinDescripcion] = useState(false);
  const [filtroAntiguedad, setFiltroAntiguedad] = useState('todas'); // 'todas' | 'mias' | 'antiguas'

  // Buscador de índices históricos (entre los cientos de miles importados sin reserva digital)
  const [busqueda, setBusqueda] = useState({ tipo_registro: '', foja: '', numero: '', anio: '', q: '' });

  // Filtro de fecha para "Completar Foja Reservada": por defecto, hasta 6 meses atrás
  const seisMesesAtras = new Date();
  seisMesesAtras.setMonth(seisMesesAtras.getMonth() - 6);
  const [fechaDesdeReserva, setFechaDesdeReserva] = useState(seisMesesAtras.toISOString().slice(0, 10));

  // Estado Cabecera
  const [registro, setRegistro] = useState('propiedad');
  const [nombreSociedad, setNombreSociedad] = useState('');
  const [rutSociedad, setRutSociedad] = useState('');
  const [naturalezaSelection, setNaturalezaSelection] = useState('');
  const [catalogoNaturalezas, setCatalogoNaturalezas] = useState([]);
  const [mostrarTodasNaturalezas, setMostrarTodasNaturalezas] = useState(false);
  const [fojaReservada, setFojaReservada] = useState(null); // ID del registro_indices
  const [guardando, setGuardando] = useState(false); // evita doble-submit (doble click dispara 2 inserts en paralelo)

  // Estado Gravamen/Propiedad Base + Datos del Inmueble (comuna/descripción/roles de avalúo)
  const INMUEBLE_BASE_VACIO = {
    foja_base: '', numero_base: '', anio_base: '',
    comuna: '', descripcion: '', rol_avaluo: '', rol_avaluo_2: '', rol_avaluo_3: '', rol_avaluo_4: ''
  };
  const [inmuebleBase, setInmuebleBase] = useState(INMUEBLE_BASE_VACIO);
  const [datosInmuebleBase, setDatosInmuebleBase] = useState(null);

  // Estado Intervinientes (en comercio son opcionales como socios adicionales)
  const [intervinientes, setIntervinientes] = useState([
    { id: 1, rol: 'ADQUIRENTE', es_razon_social: false, nombres: '', apellido_paterno: '', apellido_materno: '', rut: '' }
  ]);

  // Cargar Catálogo (Lookup Naturaleza de Acto)
  useEffect(() => {
    const cargarCat = async () => {
      try {
        const { data } = await api.get('/ingreso-indice/naturalezas');
        if (data && data.data) {
          setCatalogoNaturalezas(data.data);
        }
      } catch (error) {
        console.error("Error al cargar naturalezas:", error);
      }
    };
    cargarCat();
  }, []);

  // Opciones filtradas dinámicamente según el registro activo (en comercio se ocultan actos ajenos)
  const opcionesNaturalezas = useMemo(() => {
    if (registro === 'comercio' && !mostrarTodasNaturalezas) {
      return catalogoNaturalezas.filter(cat => {
        const cod = (cat.abreviatura || '').toUpperCase();
        const nom = (cat.nombre || '').toUpperCase();
        return CODIGOS_COMERCIO.includes(cod) ||
               nom.includes('SOCIEDAD') ||
               nom.includes('ESTATUTO') ||
               nom.includes('DIRECTORIO') ||
               nom.includes('PODER') ||
               nom.includes('MANDATO') ||
               nom.includes('TRANSFORMACION') ||
               nom.includes('DISOLUCION');
      });
    }
    return catalogoNaturalezas;
  }, [catalogoNaturalezas, registro, mostrarTodasNaturalezas]);

  // Cargar Reservas Pendientes de completar con datos.
  // "Completar Foja Reservada" se acota a los últimos 6 meses por defecto (ajustable);
  // "Ingresar Índice Histórico" no tiene tope de fecha, porque busca en el import legacy
  // completo y la fecha de creación del registro no refleja el año real del trámite.
  const cargarPendientes = async (overrides = {}) => {
    setLoadingPendientes(true);
    try {
      const b = { ...busqueda, ...overrides };
      const origen = overrides.origen ?? (modo === 'reserva' ? 'reservada' : modo === 'legacy' ? 'legacy' : undefined);
      const fechaDesde = overrides.fecha_desde ?? (origen === 'reservada' ? fechaDesdeReserva : undefined);
      const { data } = await api.get('/fojas/pendientes', {
        params: {
          todas_fechas: origen === 'reservada' ? undefined : 1,
          fecha_desde: fechaDesde,
          origen,
          tipo_registro: b.tipo_registro || undefined,
          foja: b.foja || undefined,
          numero: b.numero || undefined,
          anio: b.anio || undefined,
          q: b.q || undefined,
        }
      });
      if (data && data.status === 'success') {
        setPendientes(data.data);
      }
    } catch (error) {
      console.error("Error al cargar fojas pendientes:", error);
    } finally {
      setLoadingPendientes(false);
    }
  };

  const handleBuscarPendientes = (e) => {
    e.preventDefault();
    cargarPendientes();
  };

  // Qué le falta a un índice para considerarse completo
  const datosFaltantes = (item) => {
    const faltantes = [];
    if ((item.intervinientes?.length || 0) === 0) faltantes.push('Sin intervinientes');
    if (!item.naturaleza_id) faltantes.push('Sin naturaleza');
    if (!(item.inmueble && item.inmueble.calle && item.inmueble.comuna)) faltantes.push('Sin descripción/comuna');
    return faltantes;
  };

  // Precarga en el formulario lo que el registro YA tiene guardado (intervinientes,
  // datos del inmueble, naturaleza), para que el usuario solo complete lo que falta
  // en vez de arrancar de cero.
  const precargarDesdeRegistro = (res) => {
    setReservaSeleccionada(res);
    setFojaReservada(res.id);
    setRegistro(res.tipo_registro);

    const esComercio = res.tipo_registro === 'comercio';

    if (esComercio) {
      const razonSocial = (res.intervinientes || []).find(i => i.es_razon_social) || (res.intervinientes || [])[0];
      setNombreSociedad(razonSocial?.nombres || res.inmueble?.calle || '');
      setRutSociedad(razonSocial?.rut || '');

      // Excluir la sociedad principal para que no aparezca duplicada como interviniente/socio
      const sociosAdicionales = (res.intervinientes || []).filter(i => i.id !== razonSocial?.id);
      setIntervinientes(
        sociosAdicionales.length > 0
          ? sociosAdicionales.map(i => ({
              id: i.id,
              rol: normalizarRol(i.rol),
              es_razon_social: !!i.es_razon_social,
              nombres: i.nombres || '',
              apellido_paterno: i.apellido_paterno || '',
              apellido_materno: i.apellido_materno || '',
              rut: i.rut || ''
            }))
          : []
      );
    } else {
      setNombreSociedad('');
      setRutSociedad('');
      setIntervinientes(
        res.intervinientes && res.intervinientes.length > 0
          ? res.intervinientes.map(i => ({
              id: i.id,
              rol: normalizarRol(i.rol),
              es_razon_social: !!i.es_razon_social,
              nombres: i.nombres || '',
              apellido_paterno: i.apellido_paterno || '',
              apellido_materno: i.apellido_materno || '',
              rut: i.rut || ''
            }))
          : [{ id: 1, rol: res.tipo_registro === 'propiedad' ? 'ADQUIRENTE' : 'DEUDOR', es_razon_social: false, nombres: '', apellido_paterno: '', apellido_materno: '', rut: '' }]
      );
    }

    setInmuebleBase({
      foja_base: '', numero_base: '', anio_base: '',
      comuna: res.inmueble?.comuna || '',
      descripcion: res.inmueble?.calle || '',
      rol_avaluo: res.inmueble?.rol_avaluo || '',
      rol_avaluo_2: res.inmueble?.rol_avaluo_2 || '',
      rol_avaluo_3: res.inmueble?.rol_avaluo_3 || '',
      rol_avaluo_4: res.inmueble?.rol_avaluo_4 || ''
    });

    setNaturalezaSelection(res.naturaleza_id || '');
  };

  // Cargar una reserva específica si viene por URL
  const cargarReservaEspecifica = async (id) => {
    try {
      const { data } = await api.get(`/ingreso-indice/registro-indice/${id}`);
      if (data && data.status === 'success') {
        const res = data.data;
        precargarDesdeRegistro(res);

        const rangoFoja = res.foja_termino && res.foja_termino !== res.foja_inicio
          ? `${res.foja_inicio}-${res.foja_termino}`
          : res.foja_inicio;
        toast.success(`Foja pre-cargada: F: ${rangoFoja} | N: ${res.numero} (${res.tipo_registro.toUpperCase()})`);
      }
    } catch (error) {
      console.error("Error al cargar reserva específica:", error);
      toast.error("La reserva de foja indicada no fue encontrada.");
    }
  };

  useEffect(() => {
    if (paramRegistroIndiceId) {
      cargarReservaEspecifica(paramRegistroIndiceId);
    } else {
      cargarPendientes();
    }
  }, [paramRegistroIndiceId]);

  // Manejar selección manual de reserva pendiente
  const handleSeleccionarReserva = (resId) => {
    if (!resId) {
      setReservaSeleccionada(null);
      setFojaReservada(null);
      return;
    }
    const res = pendientes.find(p => p.id === Number(resId));
    if (res) {
      precargarDesdeRegistro(res);
    }
  };

  // Muestra el modal de detalle de un índice de la lista de resultados, sin seleccionarlo
  const handleVerDetalle = (e, item) => {
    e.stopPropagation();
    setDetalleItem(item);
  };

  const handleDesseleccionarReserva = () => {
    setReservaSeleccionada(null);
    setFojaReservada(null);
    cargarPendientes();
  };

  // Modo histórico: cambia el tipo de registro y lo sincroniza con `registro`
  // (usado por el resto del formulario: gravámenes, roles de interviniente, etc.)
  const handleHistoricoTipoChange = (tipo) => {
    setHistorico({ ...historico, tipo_registro: tipo });
    setRegistro(tipo);
    if (tipo === 'comercio') {
      setIntervinientes([]);
    } else {
      setIntervinientes([
        { id: 1, rol: tipo === 'propiedad' ? 'ADQUIRENTE' : 'DEUDOR', es_razon_social: false, nombres: '', apellido_paterno: '', apellido_materno: '', rut: '' }
      ]);
    }
  };

  const handleCambiarModo = (nuevoModo) => {
    setModo(nuevoModo);
    if (nuevoModo === 'nuevo') {
      setRegistro(historico.tipo_registro);
      if (historico.tipo_registro === 'comercio') {
        setIntervinientes([]);
      }
    } else if (nuevoModo === 'sociedades') {
      setRegistro('comercio');
      setIntervinientes([]);
    } else if (reservaSeleccionada) {
      setRegistro(reservaSeleccionada.tipo_registro);
    }
    if (nuevoModo === 'reserva' || nuevoModo === 'legacy') {
      setBusqueda({ tipo_registro: '', foja: '', numero: '', anio: '', q: '' });
      cargarPendientes({ origen: nuevoModo === 'reserva' ? 'reservada' : 'legacy', tipo_registro: '', foja: '', numero: '', anio: '', q: '' });
    }
  };

  /**
   * Carga una inscripción previa de sociedad en el formulario activo
   * para ingresar un nuevo acto (Modificación, Transformación o Disolución)
   */
  const handleCargarSociedadEnFormulario = (item, cerrarModal = false) => {
    setRegistro('comercio');
    setHistorico(prev => ({
      ...prev,
      tipo_registro: 'comercio',
      anio: prev.anio || String(new Date().getFullYear())
    }));

    setNombreSociedad(item.nombre_de_la_sociedad || '');
    setRutSociedad(item.rut || '');

    // Cargar socios adicionales (excluyendo la razón social para no duplicarla)
    if (item.intervinientes && item.intervinientes.length > 0) {
      const sociosAdicionales = item.intervinientes.filter(int => {
        const nom = (int.nombres || int.nombre_completo || '').toUpperCase();
        return nom && nom !== (item.nombre_de_la_sociedad || '').toUpperCase() && !int.es_razon_social;
      }).map((int, idx) => {
        let rolNorm = normalizarRol(int.rol);
        if (!['ADQUIRENTE', 'ENAJENANTE'].includes(rolNorm)) {
          rolNorm = 'ADQUIRENTE';
        }
        return {
          id: idx + 1,
          rol: rolNorm,
          es_razon_social: Boolean(int.es_razon_social),
          nombres: int.nombres || int.nombre_completo || '',
          apellido_paterno: int.apellido_paterno || '',
          apellido_materno: int.apellido_materno || '',
          rut: int.rut || ''
        };
      });
      setIntervinientes(sociosAdicionales);
    } else {
      setIntervinientes([]);
    }

    // Asignar foja, número y año anterior como referencia de propiedad base
    setInmuebleBase(prev => ({
      ...prev,
      foja_base: String(item.fojas),
      numero_base: String(item.numero),
      anio_base: String(item.ano),
      descripcion: prev.descripcion || `Ref. a ${item.tipo_de_contrato} (Fs. ${item.fojas} N° ${item.numero} Año ${item.ano})`
    }));

    if (cerrarModal) {
      setModalSociedades(false);
    } else {
      setModo('nuevo');
      setHistoricoConfirmado(true);
    }

    toast.success(`Datos de "${item.nombre_de_la_sociedad}" y cita anterior cargados en el formulario.`);
  };

  const handleResetHistorico = () => {
    setHistorico({ tipo_registro: 'propiedad', foja_inicio: '', foja_termino: '', numero: '', anio: '', fecha_inscripcion: '' });
    setHistoricoConfirmado(false);
    setNombreSociedad('');
    setRutSociedad('');
  };

  const handleConfirmarHistorico = (e) => {
    e.preventDefault();
    if (!historico.foja_inicio || !historico.numero || !historico.anio) {
      toast.error('Debe completar Foja, Número y Año.');
      return;
    }
    setHistoricoConfirmado(true);
  };

  // Lookup de Propiedad Base (Gravámenes) — autocompleta Comuna/Descripción/Rol de Avalúo si los encuentra
  const handleLookupPropiedad = async (e) => {
    e.preventDefault();
    if (!inmuebleBase.foja_base || !inmuebleBase.numero_base || !inmuebleBase.anio_base) {
      toast.error('Debe completar Foja, Nro y Año Base para buscar.');
      return;
    }
    const reqToast = toast.loading('Buscando propiedad base...');
    try {
      const { data } = await api.post('/ingreso-indice/lookup-propiedad', {
        foja: inmuebleBase.foja_base,
        numero: inmuebleBase.numero_base,
        anio: inmuebleBase.anio_base,
        tipo_registro: registro
      });
      setDatosInmuebleBase(data.data);
      setInmuebleBase(prev => ({
        ...prev,
        comuna: COMUNAS.includes(data.data.comuna) ? data.data.comuna : prev.comuna,
        descripcion: prev.descripcion || data.data.direccion || '',
        rol_avaluo: prev.rol_avaluo || data.data.rol_avaluo || '',
        rol_avaluo_2: prev.rol_avaluo_2 || data.data.rol_avaluo_2 || '',
        rol_avaluo_3: prev.rol_avaluo_3 || data.data.rol_avaluo_3 || '',
        rol_avaluo_4: prev.rol_avaluo_4 || data.data.rol_avaluo_4 || ''
      }));
      toast.success('Inmueble encontrado y autocompletado', { id: reqToast });
    } catch (error) {
      setDatosInmuebleBase(null);
      toast.error('No se encontró el inmueble referenciado', { id: reqToast });
    }
  };

  // Gestión de Intervinientes
  const handleAddInterviniente = (e) => {
    e.preventDefault();
    setIntervinientes([
      ...intervinientes,
      { id: Date.now(), rol: registro === 'propiedad' ? 'ADQUIRENTE' : 'DEUDOR', es_razon_social: false, nombres: '', apellido_paterno: '', apellido_materno: '', rut: '' }
    ]);
  };

  const handleIntervinienteChange = (id, field, value) => {
    setIntervinientes(intervinientes.map(int => {
      if (int.id === id) {
        let val = value;
        if (field !== 'es_razon_social') {
          val = typeof value === 'string' ? value.toUpperCase() : value;
        }
        return { ...int, [field]: val };
      }
      return int;
    }));
  };

  const handleRemoveInterviniente = (e, id) => {
    e.preventDefault();
    setIntervinientes(intervinientes.filter(int => int.id !== id));
  };

  const handleGuardarRegistro = async (e) => {
    e.preventDefault();
    if (guardando) return;
    if (modo === 'reserva' && !fojaReservada) {
      toast.error('Debe seleccionar o cargar una foja reservada primero');
      return;
    }
    if (modo === 'nuevo' && (!historico.foja_inicio || !historico.numero || !historico.anio)) {
      toast.error('Debe completar Foja, Número y Año del índice.');
      return;
    }

    const esComercio = registro === 'comercio';

    if (esComercio) {
      if (!nombreSociedad.trim()) {
        toast.error('Debe ingresar el Nombre y/o Razón Social de la Sociedad.');
        return;
      }
      if (!naturalezaSelection) {
        toast.error('Debe seleccionar el Tipo de Contrato / Acto de Comercio.');
        return;
      }
    } else {
      if (!inmuebleBase.comuna || !inmuebleBase.descripcion) {
        toast.error('Debe completar Comuna y Descripción del bien raíz.');
        return;
      }
      const tieneIntervinienteValido = intervinientes.some(i => (i.nombres || '').trim() !== '');
      if (!tieneIntervinienteValido) {
        toast.error('Debe ingresar al menos un interviniente con nombre.');
        return;
      }
    }

    // Preparar intervinientes consolidados
    let intervinientesFinales = [];
    if (esComercio) {
      intervinientesFinales.push({
        rol: 'ADQUIRENTE',
        es_razon_social: true,
        nombres: nombreSociedad.trim().toUpperCase(),
        apellido_paterno: '',
        apellido_materno: '',
        rut: rutSociedad.trim() || ''
      });
      // Añadir socios adicionales si el funcionario los ingresó
      (intervinientes || []).forEach(i => {
        const nom = (i.nombres || '').trim();
        if (nom && nom.toUpperCase() !== nombreSociedad.trim().toUpperCase()) {
          intervinientesFinales.push({
            rol: i.rol || 'ADQUIRENTE',
            es_razon_social: Boolean(i.es_razon_social),
            nombres: nom.toUpperCase(),
            apellido_paterno: i.es_razon_social ? '' : (i.apellido_paterno || '').trim().toUpperCase(),
            apellido_materno: i.es_razon_social ? '' : (i.apellido_materno || '').trim().toUpperCase(),
            rut: (i.rut || '').trim()
          });
        }
      });
    } else {
      intervinientesFinales = intervinientes.filter(i => (i.nombres || '').trim() !== '');
    }

    // Inmueble payload adaptativo
    const inmueblePayload = esComercio ? {
      comuna: inmuebleBase.comuna || 'San Carlos',
      descripcion: nombreSociedad.trim(),
      rut_sociedad: rutSociedad.trim() || null,
      rol_avaluo: null,
      foja_base: inmuebleBase.foja_base || null,
      numero_base: inmuebleBase.numero_base || null,
      anio_base: inmuebleBase.anio_base || null,
    } : inmuebleBase;

    const reqToast = toast.loading(
      esComercio 
        ? 'Guardando índice de comercio...' 
        : (modo === 'nuevo' ? 'Ingresando índice nuevo...' : 'Guardando índice definitivo...')
    );
    setGuardando(true);
    try {
      if (modo === 'nuevo') {
        await api.post('/ingreso-indice/guardar-sin-reserva', {
          tipo_registro: historico.tipo_registro,
          foja_inicio: historico.foja_inicio,
          foja_termino: historico.foja_termino || historico.foja_inicio,
          numero: historico.numero,
          anio: historico.anio,
          fecha_inscripcion: historico.fecha_inscripcion || null,
          naturaleza_id: naturalezaSelection || null,
          nombre_sociedad: esComercio ? nombreSociedad.trim() : null,
          inmueble: inmueblePayload,
          intervinientes: intervinientesFinales
        });
        toast.success(esComercio ? 'Índice de sociedad ingresado correctamente' : 'Índice histórico ingresado correctamente', { id: reqToast });
        handleResetHistorico();
      } else {
        await api.post('/ingreso-indice/guardar', {
          registro_indice_id: fojaReservada,
          naturaleza_id: naturalezaSelection || null,
          nombre_sociedad: esComercio ? nombreSociedad.trim() : null,
          inmueble: inmueblePayload,
          intervinientes: intervinientesFinales
        });
        toast.success(esComercio ? 'Índice de comercio completado correctamente' : 'Registro completado de forma definitiva', { id: reqToast });
        setReservaSeleccionada(null);
        setFojaReservada(null);
      }

      // Limpiar estados comunes
      setNombreSociedad('');
      setRutSociedad('');
      setIntervinientes(esComercio ? [] : [{ id: 1, rol: 'ADQUIRENTE', es_razon_social: false, nombres: '', apellido_paterno: '', apellido_materno: '', rut: '' }]);
      setInmuebleBase(INMUEBLE_BASE_VACIO);
      setDatosInmuebleBase(null);
      setNaturalezaSelection('');

      if (paramRegistroIndiceId) {
        navigate('/solicitudes');
      } else {
        cargarPendientes();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al guardar el índice', { id: reqToast });
    } finally {
      setGuardando(false);
    }
  };

  const esGravamen = ['hipotecas', 'prohibiciones', 'hipotecas_aguas', 'prohibiciones_aguas'].includes(registro);
  const stepInmueble = 2;
  const stepPropiedadBase = 3;
  const stepIntervinientes = esGravamen ? 4 : 3;

  const mostrarFormulario = (modo === 'reserva' || modo === 'legacy') ? !!reservaSeleccionada : historicoConfirmado;

  // Fojas pendientes de completar, según los filtros activos de la barra superior
  const pendientesFiltrados = pendientes.filter(item => {
    if (filtroSinIntervinientes && (item.intervinientes?.length || 0) > 0) return false;
    if (filtroSinNaturaleza && item.naturaleza_id) return false;
    if (filtroSinDescripcion && item.inmueble && item.inmueble.calle && item.inmueble.comuna) return false;
    if (filtroAntiguedad === 'mias' && item.usuario_responsable_id !== user?.id) return false;
    if (filtroAntiguedad === 'antiguas' && (Date.now() - new Date(item.created_at).getTime()) < TRES_DIAS_MS) return false;
    return true;
  });

  return (
    <div className="container" style={{ paddingBottom: '4rem', maxWidth: '1000px' }}>
      <header style={{ marginBottom: '2rem', marginTop: '1rem', display: 'flex', alignItems: 'center', gap: '15px' }}>
        {paramRegistroIndiceId && (
          <button onClick={() => navigate('/solicitudes')} className="icon-btn" style={{ padding: '8px', border: '1px solid var(--border-color)', borderRadius: '8px' }}>
            <ArrowLeft size={18} />
          </button>
        )}
        <div>
          <h1 className="section-title">Ingreso de Índices</h1>
          <p className="section-subtitle">Completa la inscripción ingresando intervinientes y referencias físicas.</p>
        </div>
      </header>

      {/* ── Modo: completar una foja que yo reservé, buscar un índice histórico, ingresar uno nuevo desde cero, o consultar sociedades ── */}
      <div style={{ display: 'flex', border: '1px solid var(--border-color)', borderRadius: '10px', overflow: 'hidden', marginBottom: '1.5rem', maxWidth: '850px' }}>
        <button
          type="button"
          onClick={() => handleCambiarModo('reserva')}
          style={{
            flex: 1, padding: '10px 14px', fontSize: '0.82rem', fontWeight: 700, border: 'none', cursor: 'pointer',
            background: modo === 'reserva' ? 'var(--primary)' : 'transparent',
            color: modo === 'reserva' ? 'white' : 'var(--text-muted)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
          }}
        >
          <CheckCircle size={15} />
          <span>Completar Foja Reservada</span>
        </button>
        <button
          type="button"
          onClick={() => handleCambiarModo('legacy')}
          style={{
            flex: 1, padding: '10px 14px', fontSize: '0.82rem', fontWeight: 700, border: 'none', cursor: 'pointer',
            background: modo === 'legacy' ? 'var(--primary)' : 'transparent',
            color: modo === 'legacy' ? 'white' : 'var(--text-muted)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
          }}
        >
          <Clock size={15} />
          <span>Ingresar Índice Histórico</span>
        </button>
        <button
          type="button"
          onClick={() => handleCambiarModo('nuevo')}
          style={{
            flex: 1, padding: '10px 14px', fontSize: '0.82rem', fontWeight: 700, border: 'none', cursor: 'pointer',
            background: modo === 'nuevo' ? 'var(--primary)' : 'transparent',
            color: modo === 'nuevo' ? 'white' : 'var(--text-muted)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
          }}
        >
          <Plus size={15} />
          <span>Ingresar desde Cero</span>
        </button>
        <button
          type="button"
          onClick={() => handleCambiarModo('sociedades')}
          style={{
            flex: 1, padding: '10px 14px', fontSize: '0.82rem', fontWeight: 700, border: 'none', cursor: 'pointer',
            background: modo === 'sociedades' ? 'var(--primary)' : 'transparent',
            color: modo === 'sociedades' ? 'white' : 'var(--text-muted)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
          }}
        >
          <Building2 size={15} />
          <span>Historial Sociedades</span>
        </button>
      </div>

      {/* ── 0. Foja ya vinculada: mismo cuadro de confirmación para cualquiera de los 2 modos que la encontró ── */}
      {(modo === 'reserva' || modo === 'legacy') && reservaSeleccionada && (
        <section className="glass-card animate-scale-in" style={{
          padding: '1.2rem 1.5rem',
          border: '1px solid rgba(16, 185, 129, 0.2)',
          background: 'rgba(16, 185, 129, 0.03)',
          borderRadius: '12px',
          marginBottom: '2rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <div style={{ color: 'var(--success)' }}>
              <CheckCircle size={28} />
            </div>
            <div>
              <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', fontWeight: 600 }}>
                Foja Vinculada Correctamente
              </div>
              <div style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--primary-dark)', marginTop: '2px' }}>
                {BOOKS_MAP[reservaSeleccionada.tipo_registro] || reservaSeleccionada.tipo_registro} — Foja {reservaSeleccionada.foja_inicio}{reservaSeleccionada.foja_termino !== reservaSeleccionada.foja_inicio ? `-${reservaSeleccionada.foja_termino}` : ''} | Número {reservaSeleccionada.numero} | Año {reservaSeleccionada.anio}
              </div>
              {reservaSeleccionada.solicitud && (
                <div style={{ fontSize: '0.75rem', color: 'var(--primary)', fontWeight: 600, marginTop: '2px' }}>
                  Vinculado a Solicitud/Trámite #{reservaSeleccionada.solicitud.id} ({reservaSeleccionada.solicitud.tipo_tramite})
                </div>
              )}
            </div>
          </div>
          {!paramRegistroIndiceId && (
            <button className="btn btn-sm btn-secondary" onClick={handleDesseleccionarReserva}>
              Cambiar Foja
            </button>
          )}
        </section>
      )}

      {/* ── 0a. Completar Foja Reservada: lista simple de lo que YO tomé, sin buscador ── */}
      {modo === 'reserva' && !reservaSeleccionada && (
        <section className="glass-card animate-fade-in" style={{ padding: '2rem', marginBottom: '2rem', border: '1px dashed var(--border-color)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.2rem' }}>
            <div>
              <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--primary-dark)', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Clock size={18} /> Seleccionar Foja Reservada
              </h2>
              <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '4px 0 0 0' }}>
                Debes vincular este registro a una foja previamente tomada por el sistema.
              </p>
            </div>
            <button className="btn btn-secondary btn-sm" onClick={() => cargarPendientes()} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <RefreshCw size={12} /> Actualizar
            </button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '1.2rem' }}>
            <label style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600, margin: 0 }}>Reservadas desde:</label>
            <input
              type="date"
              value={fechaDesdeReserva}
              onChange={(e) => {
                setFechaDesdeReserva(e.target.value);
                cargarPendientes({ fecha_desde: e.target.value });
              }}
              style={{ fontSize: '0.78rem', padding: '5px 8px' }}
            />
          </div>

          {loadingPendientes ? (
            <div style={{ textAlign: 'center', padding: '1.5rem 0', color: 'var(--text-muted)', fontSize: '0.82rem' }}>
              Cargando reservas pendientes...
            </div>
          ) : pendientes.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem 1rem', background: 'rgba(0,0,0,0.01)', borderRadius: '8px' }}>
              <AlertCircle size={24} style={{ color: 'var(--copper)', marginBottom: '8px', display: 'inline-block' }} />
              <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-main)', fontWeight: 600 }}>No tienes fojas reservadas pendientes.</p>
              <p style={{ margin: '4px 0 1rem 0', fontSize: '0.75rem', color: 'var(--text-muted)' }}>Debes tomar fojas primero desde el tomador de fojas.</p>
              <Link to="/tomador-fojas" className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.8rem', padding: '6px 14px' }}>
                Ir al Tomador de Fojas <ExternalLink size={14} />
              </Link>
            </div>
          ) : (
            <div className="form-group">
              <label style={{ fontWeight: 600 }}>Fojas Reservadas Disponibles ({pendientes.length})</label>
              <select onChange={(e) => handleSeleccionarReserva(e.target.value)} defaultValue="" style={{ fontSize: '0.88rem', padding: '10px' }}>
                <option value="">-- Selecciona una foja reservada --</option>
                {pendientes.map(item => (
                  <option key={item.id} value={item.id}>
                    [{BOOKS_MAP[item.tipo_registro] || item.tipo_registro}] - Foja: {item.foja_inicio}{item.foja_termino !== item.foja_inicio ? `-${item.foja_termino}` : ''} | Nro: {item.numero} | Año: {item.anio} {item.solicitud ? `(Solicitud #${item.solicitud.id})` : '(Ingreso Libre)'}
                  </option>
                ))}
              </select>
              <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '6px', display: 'block' }}>
                ¿No encuentras la foja? <Link to="/tomador-fojas" style={{ color: 'var(--primary)', fontWeight: 600 }}>Toma nuevas fojas aquí</Link>
              </span>
            </div>
          )}
        </section>
      )}

      {/* ── 0b. Ingresar Índice Histórico: buscador entre los índices importados del sistema anterior ── */}
      {modo === 'legacy' && !reservaSeleccionada && (
        <section className="glass-card animate-fade-in" style={{ padding: '2rem', marginBottom: '2rem', border: '1px dashed var(--border-color)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.2rem' }}>
            <div>
              <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--primary-dark)', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Search size={18} /> Buscar Índice Histórico
              </h2>
              <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '4px 0 0 0' }}>
                Busca por Foja/Número/Año (o apellido) entre los índices importados del sistema anterior, nunca reservados digitalmente, y ve qué datos le faltan.
              </p>
            </div>
            <button className="btn btn-secondary btn-sm" onClick={() => cargarPendientes()} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <RefreshCw size={12} /> Actualizar
            </button>
          </div>

          {/* Buscador */}
          <form onSubmit={handleBuscarPendientes} className="grid-4" style={{ alignItems: 'flex-end', marginBottom: '1rem' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Registro</label>
              <select value={busqueda.tipo_registro}
                onChange={(e) => setBusqueda({ ...busqueda, tipo_registro: e.target.value })}>
                <option value="">Todos</option>
                {Object.entries(BOOKS_MAP).map(([id, nombre]) => <option key={id} value={id}>{nombre}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Foja</label>
              <input type="number" placeholder="Ej: 450" value={busqueda.foja}
                onChange={(e) => setBusqueda({ ...busqueda, foja: e.target.value })} />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Número</label>
              <input type="number" placeholder="Ej: 452" value={busqueda.numero}
                onChange={(e) => setBusqueda({ ...busqueda, numero: e.target.value })} />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Año</label>
              <input type="number" placeholder="Ej: 2019" value={busqueda.anio}
                onChange={(e) => setBusqueda({ ...busqueda, anio: e.target.value })} />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Apellido / RUT</label>
              <input type="text" placeholder="Ej: González" value={busqueda.q}
                onChange={(e) => setBusqueda({ ...busqueda, q: e.target.value })} />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <button type="submit" className="btn btn-primary btn-sm">
                <Search size={14} style={{ marginRight: '4px' }} /> Buscar
              </button>
            </div>
          </form>

          {loadingPendientes ? (
            <div style={{ textAlign: 'center', padding: '1.5rem 0', color: 'var(--text-muted)', fontSize: '0.82rem' }}>
              Buscando...
            </div>
          ) : pendientes.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem 1rem', background: 'rgba(0,0,0,0.01)', borderRadius: '8px' }}>
              <AlertCircle size={24} style={{ color: 'var(--copper)', marginBottom: '8px', display: 'inline-block' }} />
              <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-main)', fontWeight: 600 }}>No se encontraron índices con esos criterios.</p>
              <p style={{ margin: '4px 0 1rem 0', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                ¿Es una foja que nunca existió como registro? Usa "Ingresar desde Cero" arriba.
              </p>
            </div>
          ) : (
            <div className="form-group">
              {/* Filtros: qué le falta al índice, y qué tan antigua es la reserva */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', marginBottom: '1rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.72rem', color: 'var(--text-muted)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={filtroSinIntervinientes} onChange={(e) => setFiltroSinIntervinientes(e.target.checked)} />
                  Sin intervinientes
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.72rem', color: 'var(--text-muted)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={filtroSinNaturaleza} onChange={(e) => setFiltroSinNaturaleza(e.target.checked)} />
                  Sin naturaleza del acto
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.72rem', color: 'var(--text-muted)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={filtroSinDescripcion} onChange={(e) => setFiltroSinDescripcion(e.target.checked)} />
                  Sin descripción/comuna
                </label>
              </div>

              <label style={{ fontWeight: 600, display: 'block', marginBottom: '8px' }}>
                Resultados ({pendientesFiltrados.length} de {pendientes.length})
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '420px', overflowY: 'auto' }}>
                {pendientesFiltrados.map(item => {
                  const faltantes = datosFaltantes(item);
                  return (
                    <div
                      key={item.id}
                      onClick={() => handleSeleccionarReserva(item.id)}
                      style={{
                        textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px',
                        padding: '10px 14px', borderRadius: '8px', border: '1px solid var(--border-color)',
                        background: 'var(--bg-surface)', cursor: 'pointer'
                      }}
                    >
                      <div>
                        <div style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--primary-dark)' }}>
                          [{BOOKS_MAP[item.tipo_registro] || item.tipo_registro}] Foja {item.foja_inicio}{item.foja_termino !== item.foja_inicio ? `-${item.foja_termino}` : ''} · Número {item.numero} · Año {item.anio}
                        </div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '2px' }}>
                          {item.solicitud ? `Solicitud #${item.solicitud.id}` : 'Ingreso Libre'}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', justifyContent: 'flex-end', maxWidth: '220px' }}>
                          {faltantes.length === 0 ? (
                            <span className="badge badge-success" style={{ fontSize: '0.62rem' }}>Datos completos</span>
                          ) : faltantes.map(f => (
                            <span key={f} className="badge badge-warning" style={{ fontSize: '0.62rem' }}>{f}</span>
                          ))}
                        </div>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={(e) => handleVerDetalle(e, item)}
                          style={{ padding: '4px 8px', fontSize: '0.72rem', display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}
                          title="Ver todos los datos ya ingresados de este índice"
                        >
                          <Eye size={13} /> Ver detalle
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </section>
      )}

      {/* ── 0c. Ingresar desde Cero (sin reserva previa, con validación de duplicado) ── */}
      {modo === 'nuevo' && (
        historicoConfirmado ? (
          <section className="glass-card animate-scale-in" style={{
            padding: '1.2rem 1.5rem',
            border: '1px solid rgba(180, 83, 9, 0.25)',
            background: 'rgba(180, 83, 9, 0.04)',
            borderRadius: '12px',
            marginBottom: '2rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              <div style={{ color: 'var(--copper)' }}>
                <Clock size={28} />
              </div>
              <div>
                <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', fontWeight: 600 }}>
                  Índice Nuevo (sin reserva)
                </div>
                <div style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--primary-dark)', marginTop: '2px' }}>
                  {BOOKS_MAP[historico.tipo_registro]} — Foja {historico.foja_inicio}{historico.foja_termino && historico.foja_termino !== historico.foja_inicio ? `-${historico.foja_termino}` : ''} | Número {historico.numero} | Año {historico.anio}
                </div>
              </div>
            </div>
            <button className="btn btn-sm btn-secondary" onClick={handleResetHistorico}>
              Cambiar Datos
            </button>
          </section>
        ) : (
          <section className="glass-card animate-fade-in" style={{ padding: '2rem', marginBottom: '2rem', border: '1px dashed var(--border-color)' }}>
            <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--primary-dark)', margin: '0 0 4px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Clock size={18} /> Ingresar Índice desde Cero
            </h2>
            <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '0 0 1.2rem 0' }}>
              Para un registro que nunca existió en el sistema. Al continuar, se valida que no exista ya un índice con ese Registro/Foja/Número/Año.
            </p>
            <form onSubmit={handleConfirmarHistorico}>
              <div className="grid-3">
                <div className="form-group">
                  <label>Registro</label>
                  <select value={historico.tipo_registro} onChange={(e) => handleHistoricoTipoChange(e.target.value)}>
                    {Object.entries(BOOKS_MAP).map(([id, nombre]) => <option key={id} value={id}>{nombre}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Foja Inicio</label>
                  <input type="number" placeholder="Ej: 100" value={historico.foja_inicio}
                    onChange={(e) => setHistorico({ ...historico, foja_inicio: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Foja Término (opcional)</label>
                  <input type="number" placeholder="Igual a Foja Inicio si se omite" value={historico.foja_termino}
                    onChange={(e) => setHistorico({ ...historico, foja_termino: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Número</label>
                  <input type="number" placeholder="Ej: 100" value={historico.numero}
                    onChange={(e) => setHistorico({ ...historico, numero: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Año</label>
                  <input type="number" placeholder="Ej: 2019" value={historico.anio}
                    onChange={(e) => setHistorico({ ...historico, anio: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Fecha de Inscripción (opcional)</label>
                  <input type="date" value={historico.fecha_inscripcion}
                    onChange={(e) => setHistorico({ ...historico, fecha_inscripcion: e.target.value })} />
                </div>
              </div>
              <button type="submit" className="btn btn-primary" style={{ marginTop: '0.5rem' }}>
                Continuar
              </button>
            </form>
          </section>
        )
      )}

      {/* ── 0d. Historial y Consulta de Sociedades (Comercio) ── */}
      {modo === 'sociedades' && (
        <section className="glass-card animate-fade-in" style={{ padding: '2rem', marginBottom: '2rem', border: '1px solid var(--border-color)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.2rem' }}>
            <div>
              <h2 style={{ fontSize: '1.15rem', fontWeight: 700, color: 'var(--primary-dark)', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Building2 size={20} /> Historial de Sociedades & Registro de Comercio
              </h2>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: '4px 0 0 0' }}>
                Consulta inscripciones anteriores (Constitución, Modificación, Transformación, Disolución) y cárgalas automáticamente en el formulario de nuevo acto.
              </p>
            </div>
            {haBuscadoSociedad && (
              <button type="button" className="btn btn-secondary btn-sm" onClick={limpiarSociedad} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <RotateCcw size={13} /> Limpiar
              </button>
            )}
          </div>

          {/* Formulario de Búsqueda */}
          <form 
            onSubmit={(e) => {
              e.preventDefault();
              consultarSociedad();
            }} 
            style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '1rem' }}
          >
            <div style={{ flex: '1 1 320px', position: 'relative' }}>
              <input
                type="text"
                placeholder="Ingresa la razón social (ej: Agrícola El Roble SpA, Inversiones Los Andes)..."
                value={busquedaSociedad}
                onChange={(e) => setBusquedaSociedad(e.target.value)}
                style={{ width: '100%', padding: '9px 12px', fontSize: '0.88rem' }}
              />
            </div>
            <button
              type="submit"
              disabled={cargandoSociedad}
              className="btn btn-primary btn-sm"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '9px 16px' }}
            >
              {cargandoSociedad ? <RefreshCw className="spinner" size={14} /> : <Search size={14} />}
              <span>{cargandoSociedad ? 'Buscando...' : 'Consultar'}</span>
            </button>
          </form>

          {/* Filtros dinámicos por tipo de contrato */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '1.2rem' }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)' }}>Filtrar acto:</span>
            {TIPOS_CONTRATO.map((tipo) => {
              const activo = filtroContratoSociedad === tipo;
              const count = tipo === 'TODOS' ? inscripcionesSociedad.length : (conteoSociedadPorTipo[tipo] || 0);
              return (
                <button
                  key={tipo}
                  type="button"
                  onClick={() => {
                    setFiltroContratoSociedad(tipo);
                    if (haBuscadoSociedad && busquedaSociedad.trim()) {
                      consultarSociedad(busquedaSociedad, tipo);
                    }
                  }}
                  style={{
                    padding: '3px 9px',
                    borderRadius: '12px',
                    border: activo ? '1px solid var(--primary)' : '1px solid var(--border-color)',
                    background: activo ? 'var(--primary)' : 'transparent',
                    color: activo ? 'white' : 'var(--text-main)',
                    fontSize: '0.74rem',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  {tipo} {count > 0 && `(${count})`}
                </button>
              );
            })}
          </div>

          {/* Resultados */}
          {cargandoSociedad ? (
            <div style={{ textAlign: 'center', padding: '2rem 0', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
              <RefreshCw className="spinner" size={20} style={{ margin: '0 auto 8px' }} />
              <div>Consultando libros de comercio...</div>
            </div>
          ) : !haBuscadoSociedad ? (
            <div style={{ textAlign: 'center', padding: '2rem 1rem', background: 'var(--bg-body)', borderRadius: '8px', border: '1px dashed var(--border-color)' }}>
              <Building2 size={32} style={{ color: 'var(--text-muted)', margin: '0 auto 8px' }} />
              <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-main)' }}>
                Ingresa una razón social para consultar inscripciones
              </p>
              <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                Podrás revisar asientos previos y cargarlos con un solo click en el formulario de nuevo ingreso.
              </p>
            </div>
          ) : inscripcionesSociedad.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem 1rem', background: 'var(--bg-body)', borderRadius: '8px' }}>
              <AlertCircle size={24} style={{ color: 'var(--warning)', margin: '0 auto 6px' }} />
              <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-main)' }}>
                No se encontraron inscripciones para "{busquedaSociedad}"
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '420px', overflowY: 'auto' }}>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600, marginBottom: '2px' }}>
                Historial Registral ({inscripcionesSociedad.length} inscripción{inscripcionesSociedad.length === 1 ? '' : 'es'})
              </div>
              {inscripcionesSociedad.map((item) => {
                const badge = obtenerBadgeContrato(item);
                return (
                  <div
                    key={item.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      border: '1px solid var(--border-color)',
                      background: 'var(--bg-surface)'
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                        <span style={{
                          padding: '2px 8px',
                          borderRadius: '6px',
                          fontSize: '0.72rem',
                          fontWeight: 700,
                          background: badge.bg,
                          color: badge.text,
                          border: `1px solid ${badge.border}`
                        }}>
                          {badge.label}
                        </span>
                        <strong style={{ fontSize: '0.85rem', color: 'var(--primary-dark)' }}>
                          Foja {item.fojas} · N° {item.numero} · Año {item.ano}
                        </strong>
                      </div>

                      <div style={{ fontSize: '0.9rem', color: 'var(--text-main)', fontWeight: 700, marginBottom: '6px' }}>
                        {item.nombre_de_la_sociedad}
                      </div>

                      {/* Lista de Intervinientes con Rol */}
                      {item.intervinientes && item.intervinientes.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '4px' }}>
                          {item.intervinientes.map((int, iIdx) => (
                            <span
                              key={int.id || iIdx}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                background: 'var(--bg-body)',
                                border: '1px solid var(--border-color)',
                                padding: '2px 8px',
                                borderRadius: '6px',
                                fontSize: '0.74rem',
                                color: 'var(--text-main)'
                              }}
                            >
                              <strong style={{ color: 'var(--primary)', textTransform: 'uppercase', fontSize: '0.68rem' }}>
                                {normalizarRol(int.rol)}:
                              </strong>{' '}
                              <span>{int.nombre_completo}</span>
                            </span>
                          ))}
                        </div>
                      )}

                      {item.fecha_inscripcion && (
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '4px' }}>
                          Fecha Asiento: {item.fecha_inscripcion}
                        </div>
                      )}
                    </div>

                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => {
                          navigator.clipboard.writeText(`Fs. ${item.fojas} N° ${item.numero} Año ${item.ano} (${item.tipo_de_contrato} - ${item.nombre_de_la_sociedad})`);
                          toast.success('Cita copiada al portapapeles');
                        }}
                        title="Copiar cita registral"
                        style={{ padding: '6px 10px', fontSize: '0.75rem' }}
                      >
                        <Copy size={12} />
                      </button>

                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => handleCargarSociedadEnFormulario(item)}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '6px 12px', fontSize: '0.75rem', fontWeight: 600 }}
                      >
                        <span>Cargar en Formulario</span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}

      {mostrarFormulario && (
        <form onSubmit={handleGuardarRegistro} style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>

          {/* ── 1. Clasificación y Naturaleza ── */}
          <section className="glass-card">
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '1rem', marginBottom: '1.5rem' }}>
              <span style={{ background: 'var(--primary)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', borderRadius: '50%', fontWeight: '700' }}>1</span>
              <h2 style={{ fontSize: '1.2rem', color: 'var(--primary)', margin: 0 }}>
                {registro === 'comercio' ? 'Acto Societario y Contrato' : 'Naturaleza del Acto'}
              </h2>
            </div>

            {/* Selección Rápida en 1 clic para Comercio */}
            {registro === 'comercio' && (
              <div style={{ marginBottom: '1.25rem', background: 'var(--bg-body)', padding: '12px 16px', borderRadius: '10px', border: '1px solid var(--border-color)' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Selección Rápida de Tipo de Contrato:</span>
                  <button
                    type="button"
                    onClick={() => setMostrarTodasNaturalezas(!mostrarTodasNaturalezas)}
                    style={{ background: 'none', border: 'none', color: 'var(--primary)', fontSize: '0.72rem', cursor: 'pointer', textDecoration: 'underline', fontWeight: 600 }}
                  >
                    {mostrarTodasNaturalezas ? 'Mostrar solo actos de comercio' : `Ver todo el catálogo (${catalogoNaturalezas.length})`}
                  </button>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {[
                    { key: 'Constitución', label: 'Constitución de Sociedad', match: ['CS', 'CSO', 'CONSTITUCION'] },
                    { key: 'Modificación', label: 'Modificación de Sociedad', match: ['MS', 'ME', 'MODIFICACION'] },
                    { key: 'Transformación', label: 'Transformación de Sociedad', match: ['TSO', 'TRANSFORMACION'] },
                    { key: 'Disolución', label: 'Disolución de Sociedad', match: ['DS', 'DSO', 'DYL', 'DISOLUCION'] },
                  ].map(btn => {
                    const actual = catalogoNaturalezas.find(c => c.id === naturalezaSelection);
                    const activo = actual && btn.match.some(m => actual.abreviatura === m || (actual.nombre || '').toUpperCase().includes(m));
                    return (
                      <button
                        key={btn.key}
                        type="button"
                        onClick={() => {
                          const encontrado = catalogoNaturalezas.find(c =>
                            btn.match.includes(c.abreviatura) ||
                            btn.match.some(m => (c.nombre || '').toUpperCase().includes(m))
                          );
                          if (encontrado) {
                            setNaturalezaSelection(encontrado.id);
                            toast.success(`Tipo de contrato seleccionado: ${btn.label}`);
                          }
                        }}
                        style={{
                          padding: '6px 14px',
                          fontSize: '0.78rem',
                          fontWeight: 600,
                          borderRadius: '6px',
                          border: activo ? '1px solid var(--primary)' : '1px solid var(--border-color)',
                          background: activo ? 'var(--primary)' : 'var(--bg-surface)',
                          color: activo ? '#ffffff' : 'var(--text-main)',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease'
                        }}
                      >
                        {btn.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="grid-2">
              <div className="form-group">
                <label>Registro</label>
                <input
                  type="text"
                  readOnly
                  value={BOOKS_MAP[registro] || registro}
                  style={{ textTransform: 'uppercase', background: 'var(--bg-body)', fontWeight: 600 }}
                />
              </div>

              <div className="form-group" style={{ zIndex: 50 }}>
                <label>
                  {registro === 'comercio' ? 'Tipo de Contrato / Acto de Comercio' : 'Catálogo (Naturaleza del Acto)'}
                </label>
                <Select
                  options={opcionesNaturalezas.map(cat => ({ value: cat.id, label: `${cat.abreviatura || 'N/A'} - ${cat.nombre}` }))}
                  placeholder={registro === 'comercio' ? "Selecciona o busca el acto societario..." : "Escribe naturaleza (Ej: CV, Hipoteca)"}
                  isSearchable
                  noOptionsMessage={() => "No se encontraron resultados"}
                  onChange={(selectedOption) => setNaturalezaSelection(selectedOption ? selectedOption.value : null)}
                  value={
                    naturalezaSelection
                      ? {
                          value: naturalezaSelection,
                          label: catalogoNaturalezas.find(c => c.id === naturalezaSelection)
                            ? `${catalogoNaturalezas.find(c => c.id === naturalezaSelection).abreviatura || 'N/A'} - ${catalogoNaturalezas.find(c => c.id === naturalezaSelection).nombre}`
                            : ''
                        }
                      : null
                  }
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderRadius: 'var(--radius-md)',
                      borderColor: 'var(--border-color)',
                      padding: '2px',
                      boxShadow: 'none',
                      '&:hover': { borderColor: 'var(--primary)' }
                    }),
                    option: (base, state) => ({
                      ...base,
                      backgroundColor: state.isFocused ? 'var(--info-bg)' : 'white',
                      color: state.isFocused ? 'var(--primary)' : 'var(--text-main)',
                      cursor: 'pointer'
                    })
                  }}
                />
              </div>
            </div>
          </section>

          {/* ── 2. Datos del Inmueble / Nombre de la Sociedad ── */}
          <section className="glass-card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--border-color)', paddingBottom: '1rem', marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <span style={{ background: 'var(--primary)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', borderRadius: '50%', fontWeight: '700' }}>{stepInmueble}</span>
                <h2 style={{ fontSize: '1.2rem', color: 'var(--primary)', margin: 0 }}>
                  {registro === 'comercio' ? 'Nombre de la Sociedad' : 'Datos del Inmueble'}
                </h2>
              </div>
              {registro === 'comercio' && (
                <span style={{ fontSize: '0.78rem', background: '#eff6ff', color: 'var(--primary)', padding: '4px 10px', borderRadius: '6px', fontWeight: 600 }}>
                  Registro de Comercio (Sociedades)
                </span>
              )}
            </div>

            {registro === 'comercio' ? (
              <div className="grid-3" style={{ alignItems: 'flex-start' }}>
                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <label style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-main)', marginBottom: '6px', display: 'block' }}>
                    Nombre y/o Razón Social de la Sociedad <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Ej: Agrícola y Ganadera San Carlos SpA, Inmobiliaria El Roble Ltda..."
                    value={nombreSociedad}
                    onChange={(e) => setNombreSociedad(e.target.value)}
                    required
                    style={{ fontSize: '1rem', padding: '10px 14px', borderRadius: '8px', border: '1px solid var(--border-color)', textTransform: 'uppercase', width: '100%' }}
                  />
                  <span style={{ fontSize: '0.74rem', color: 'var(--text-muted)', marginTop: '4px', display: 'block' }}>
                    Razón social o denominación con la que se registrará el índice de comercio.
                  </span>
                </div>

                <div className="form-group">
                  <label style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-main)', marginBottom: '6px', display: 'block' }}>
                    RUT de la Sociedad <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(opcional)</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Ej: 76.123.456-7"
                    value={rutSociedad}
                    onChange={(e) => setRutSociedad(e.target.value)}
                    style={{ fontSize: '1rem', padding: '10px 14px', borderRadius: '8px', border: '1px solid var(--border-color)', textTransform: 'uppercase', width: '100%' }}
                  />
                  <span style={{ fontSize: '0.74rem', color: 'var(--text-muted)', marginTop: '4px', display: 'block' }}>
                    Si se encuentra disponible en el título.
                  </span>
                </div>
              </div>
            ) : (
              <div className="grid-3">
                <div className="form-group">
                  <label>Comuna</label>
                  <select
                    value={inmuebleBase.comuna}
                    onChange={(e) => setInmuebleBase({ ...inmuebleBase, comuna: e.target.value })}
                    required
                  >
                    <option value="">Seleccione...</option>
                    {COMUNAS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <label>Descripción del Bien Raíz (lote/ubicación)</label>
                  <input
                    type="text"
                    placeholder="Ej: Lote 5, Fundo Los Aromos (sin nombre de calle domiciliaria)"
                    value={inmuebleBase.descripcion}
                    onChange={(e) => setInmuebleBase({ ...inmuebleBase, descripcion: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group" style={{ gridColumn: 'span 3' }}>
                  <label>Rol(es) de Avalúo (hasta 4, todos opcionales)</label>
                  <div className="grid-4">
                    {['rol_avaluo', 'rol_avaluo_2', 'rol_avaluo_3', 'rol_avaluo_4'].map((campo, idx) => (
                      <input
                        key={campo}
                        type="text"
                        placeholder={idx === 0 ? 'Ej: 1234-5' : `Rol ${idx + 1} (opcional)`}
                        value={inmuebleBase[campo]}
                        onChange={(e) => setInmuebleBase({ ...inmuebleBase, [campo]: e.target.value })}
                      />
                    ))}
                  </div>
                  <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>
                    Crítico para búsquedas internas y regularizaciones SII. No se muestra en la vista pública.
                  </span>
                </div>
              </div>
            )}
          </section>

          {/* ── 3. Módulo de Gravámenes (Opcional) ── */}
          {esGravamen && (
            <section className="glass-card animate-fade-in" style={{ backgroundColor: 'var(--warning-bg)', border: '1px solid #fde68a' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', borderBottom: '1px solid #fde68a', paddingBottom: '1rem', marginBottom: '1.5rem' }}>
                <span style={{ background: 'var(--warning)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', borderRadius: '50%', fontWeight: '700' }}>{stepPropiedadBase}</span>
                <h2 style={{ fontSize: '1.2rem', color: 'var(--warning)', margin: 0 }}>Propiedad Base Referenciada</h2>
              </div>

              <div className="grid-4" style={{ alignItems: 'flex-end', marginBottom: '1rem' }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Foja Base</label>
                  <input type="number" placeholder="Ej: 450"
                    value={inmuebleBase.foja_base} onChange={(e) => setInmuebleBase({...inmuebleBase, foja_base: e.target.value})} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Número Base</label>
                  <input type="number" placeholder="Ej: 452"
                    value={inmuebleBase.numero_base} onChange={(e) => setInmuebleBase({...inmuebleBase, numero_base: e.target.value})} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Año Base</label>
                  <input type="number" placeholder="Ej: 2026"
                    value={inmuebleBase.anio_base} onChange={(e) => setInmuebleBase({...inmuebleBase, anio_base: e.target.value})} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <button type="button" className="btn" style={{ background: 'var(--warning)', color: 'white' }} onClick={handleLookupPropiedad}>
                    🔍 Extraer Datos
                  </button>
                </div>
              </div>

              {datosInmuebleBase && (
                <div className="alert alert-info animate-scale-in" style={{ margin: 0, marginTop: '1rem' }}>
                  <div style={{ width: '100%' }}>
                    <p style={{ margin: 0, fontWeight: 700, color: 'var(--primary-dark)' }}>Inmueble Localizado — se autocompletaron Comuna/Descripción/Rol arriba, puedes ajustarlos.</p>
                    <ul style={{ margin: 0, marginTop: '8px', paddingLeft: '1.5rem', opacity: 0.9 }}>
                      <li><strong>Dirección:</strong> {datosInmuebleBase.direccion}</li>
                      <li><strong>Comuna:</strong> {datosInmuebleBase.comuna}</li>
                      <li><strong>Rol(es) de Avalúo:</strong> {[datosInmuebleBase.rol_avaluo, datosInmuebleBase.rol_avaluo_2, datosInmuebleBase.rol_avaluo_3, datosInmuebleBase.rol_avaluo_4].filter(Boolean).join(', ') || 'No registrado'}</li>
                    </ul>
                  </div>
                </div>
              )}
            </section>
          )}

          {/* ── 4. Matriz de Intervinientes (En comercio: Socios adicionales opcionales) ── */}
          <section className="glass-card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--border-color)', paddingBottom: '1rem', marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <span style={{ background: 'var(--primary)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', borderRadius: '50%', fontWeight: '700' }}>{stepIntervinientes}</span>
                <h2 style={{ fontSize: '1.2rem', color: 'var(--primary)', margin: 0 }}>
                  {registro === 'comercio' ? 'Socios e Intervinientes Adicionales' : 'Intervinientes'}
                </h2>
                {registro === 'comercio' && (
                  <span style={{ fontSize: '0.74rem', background: '#f1f5f9', color: '#475569', padding: '3px 8px', borderRadius: '4px', fontWeight: 600 }}>
                    Opcional
                  </span>
                )}
              </div>
              <button type="button" className="btn btn-secondary btn-sm" onClick={handleAddInterviniente}>
                {registro === 'comercio' ? '+ Agregar Socio / Representante' : '+ Agregar Parte'}
              </button>
            </div>

            {registro === 'comercio' && intervinientes.length === 0 ? (
              <div style={{ background: 'var(--bg-body)', border: '1px dashed var(--border-color)', borderRadius: 'var(--radius-md)', padding: '1.25rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                No hay socios ni representantes adicionales individualizados. La sociedad principal ya fue registrada en el paso anterior.
                <div style={{ marginTop: '6px' }}>
                  <button type="button" onClick={handleAddInterviniente} style={{ background: 'none', border: 'none', color: 'var(--primary)', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline', fontSize: '0.82rem' }}>
                    + Hacer clic aquí para agregar un socio o representante legal si lo requiere
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {intervinientes.map((int, index) => (
                  <div key={int.id} style={{ background: 'var(--bg-body)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-lg)', padding: '1.5rem', position: 'relative' }}>

                    {(intervinientes.length > 1 || registro === 'comercio') && (
                      <button type="button" className="icon-btn icon-btn-delete" style={{ position: 'absolute', top: '1rem', right: '1rem' }} onClick={(e) => handleRemoveInterviniente(e, int.id)} title="Eliminar Interviniente">
                        ✕
                      </button>
                    )}

                    <div className="grid-4" style={{ alignItems: 'center' }}>

                      {/* Selector Razón Social */}
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', background: 'var(--bg-surface)', padding: '12px 16px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-color)' }}>
                          <input
                            type="checkbox"
                            style={{ width: '18px', height: '18px', accentColor: 'var(--primary)' }}
                            checked={int.es_razon_social}
                            onChange={(e) => handleIntervinienteChange(int.id, 'es_razon_social', e.target.checked)}
                          />
                          <span>¿Es Razón Social?</span>
                        </label>
                      </div>

                      {/* Rol */}
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label>Rol Jurídico</label>
                        <select
                          style={{ textTransform: 'uppercase' }}
                          value={int.rol}
                          onChange={(e) => handleIntervinienteChange(int.id, 'rol', e.target.value)}
                          required={registro !== 'comercio'}
                        >
                          <option value="">Seleccione...</option>
                          {['propiedad', 'aguas', 'comercio', 'minas'].includes(registro) ? (
                            <><option value="ADQUIRENTE">ADQUIRENTE</option><option value="ENAJENANTE">ENAJENANTE</option></>
                          ) : (
                            <><option value="DEUDOR">DEUDOR</option><option value="ACREEDOR">ACREEDOR</option><option value="BANCO">BANCO</option></>
                          )}
                        </select>
                      </div>

                      {/* RUT */}
                      <div className="form-group" style={{ marginBottom: 0, gridColumn: 'span 2' }}>
                        <label>RUT / RUN</label>
                        <input type="text" placeholder="Ej: 12345678-9" style={{ textTransform: 'uppercase' }}
                          value={int.rut} onChange={(e) => handleIntervinienteChange(int.id, 'rut', e.target.value)}/>
                      </div>
                    </div>

                    {/* Zona de Nombres */}
                    <div className="grid-3" style={{ marginTop: '1.2rem' }}>
                      {!int.es_razon_social ? (
                        <>
                          <div className="form-group" style={{ marginBottom: 0 }}>
                            <label>Primer Apellido</label>
                            <input type="text" style={{ textTransform: 'uppercase' }}
                              value={int.apellido_paterno} onChange={(e) => handleIntervinienteChange(int.id, 'apellido_paterno', e.target.value)} required={registro !== 'comercio'} />
                          </div>
                          <div className="form-group" style={{ marginBottom: 0 }}>
                            <label>Segundo Apellido</label>
                            <input type="text" style={{ textTransform: 'uppercase' }}
                              value={int.apellido_materno} onChange={(e) => handleIntervinienteChange(int.id, 'apellido_materno', e.target.value)} />
                          </div>
                          <div className="form-group" style={{ marginBottom: 0 }}>
                            <label>Nombres</label>
                            <input type="text" style={{ textTransform: 'uppercase' }}
                              value={int.nombres} onChange={(e) => handleIntervinienteChange(int.id, 'nombres', e.target.value)} required={registro !== 'comercio'} />
                          </div>
                        </>
                      ) : (
                        <div className="form-group" style={{ marginBottom: 0, gridColumn: 'span 3' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                            <label style={{ margin: 0 }}>Nombre de Razón Social o Institución</label>
                            <button
                              type="button"
                              onClick={() => {
                                if (int.nombres) {
                                  setBusquedaSociedad(int.nombres);
                                  consultarSociedad(int.nombres);
                                }
                                setModalSociedades(true);
                              }}
                              className="btn btn-secondary btn-sm"
                              style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 8px', fontSize: '0.72rem' }}
                            >
                              <Building2 size={12} />
                              <span>Buscar Historial de esta Sociedad</span>
                            </button>
                          </div>
                          <input type="text" style={{ textTransform: 'uppercase' }} placeholder="EJ: INMOBILIARIA SAN CARLOS SPA"
                            value={int.nombres} onChange={(e) => handleIntervinienteChange(int.id, 'nombres', e.target.value)} required={registro !== 'comercio'} />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* ── Action Bar ── */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '1rem' }}>
            <button
              type="submit"
              className="btn btn-primary btn-lg"
              style={{ minWidth: '250px' }}
              disabled={guardando}
            >
              {guardando 
                ? 'Guardando...' 
                : (registro === 'comercio' ? 'Guardar Índice de Comercio ✓' : 'Guardar Inscripción Definitiva ✓')}
            </button>
          </div>

        </form>
      )}

      {/* ── Modal: Detalle completo de un índice de los resultados de búsqueda ── */}
      {detalleItem && (
        <div
          onClick={() => setDetalleItem(null)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem'
          }}
        >
          <div
            className="glass-card animate-scale-in"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '640px', width: '100%', maxHeight: '85vh', overflowY: 'auto',
              padding: '2rem', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.15)',
              border: '1px solid var(--border-color)', background: 'var(--bg-surface)'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.2rem' }}>
              <div>
                <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', fontWeight: 600 }}>
                  {BOOKS_MAP[detalleItem.tipo_registro] || detalleItem.tipo_registro}
                </div>
                <h3 style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--primary-dark)', margin: '2px 0 0 0' }}>
                  Foja {detalleItem.foja_inicio}{detalleItem.foja_termino !== detalleItem.foja_inicio ? `-${detalleItem.foja_termino}` : ''} · Número {detalleItem.numero} · Año {detalleItem.anio}
                </h3>
              </div>
              <button type="button" className="icon-btn" onClick={() => setDetalleItem(null)} style={{ padding: '6px', border: '1px solid var(--border-color)', borderRadius: '8px' }}>
                <X size={16} />
              </button>
            </div>

            {/* Naturaleza y fechas */}
            <div className="grid-2" style={{ gap: '1rem', marginBottom: '1.2rem' }}>
              <div>
                <div style={{ fontSize: '0.68rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Naturaleza del Acto</div>
                <div style={{ fontSize: '0.85rem', marginTop: '2px' }}>
                  {catalogoNaturalezas.find(c => c.id === detalleItem.naturaleza_id)?.nombre || <span style={{ fontStyle: 'italic', color: 'var(--text-muted)' }}>Sin naturaleza</span>}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '0.68rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Fecha de Inscripción</div>
                <div style={{ fontSize: '0.85rem', marginTop: '2px' }}>
                  {detalleItem.fecha_inscripcion || <span style={{ fontStyle: 'italic', color: 'var(--text-muted)' }}>No registrada</span>}
                </div>
              </div>
              {detalleItem.foja_anterior && (
                <div>
                  <div style={{ fontSize: '0.68rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Propiedad Base Referenciada</div>
                  <div style={{ fontSize: '0.85rem', marginTop: '2px' }}>
                    Foja {detalleItem.foja_anterior} · Número {detalleItem.numero_anterior} · Año {detalleItem.anio_anterior}
                  </div>
                </div>
              )}
            </div>

            {/* Inmueble */}
            <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '1rem', marginBottom: '1.2rem' }}>
              <div style={{ fontSize: '0.68rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '6px' }}>Datos del Inmueble</div>
              {detalleItem.inmueble ? (
                <div style={{ fontSize: '0.85rem' }}>
                  <div>{detalleItem.inmueble.calle}, {detalleItem.inmueble.comuna}</div>
                  {[detalleItem.inmueble.rol_avaluo, detalleItem.inmueble.rol_avaluo_2, detalleItem.inmueble.rol_avaluo_3, detalleItem.inmueble.rol_avaluo_4].filter(Boolean).length > 0 && (
                    <div style={{ color: 'var(--text-muted)', marginTop: '4px' }}>
                      Rol(es) de Avalúo: {[detalleItem.inmueble.rol_avaluo, detalleItem.inmueble.rol_avaluo_2, detalleItem.inmueble.rol_avaluo_3, detalleItem.inmueble.rol_avaluo_4].filter(Boolean).join(', ')}
                    </div>
                  )}
                </div>
              ) : (
                <span style={{ fontSize: '0.82rem', fontStyle: 'italic', color: 'var(--text-muted)' }}>Sin descripción ni comuna registradas.</span>
              )}
            </div>

            {/* Intervinientes */}
            <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '1rem', marginBottom: '1.2rem' }}>
              <div style={{ fontSize: '0.68rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '6px' }}>
                Intervinientes {detalleItem.intervinientes?.length > 0 && `(${detalleItem.intervinientes.length})`}
              </div>
              {detalleItem.intervinientes && detalleItem.intervinientes.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {detalleItem.intervinientes.map(int => (
                    <div key={int.id} style={{ fontSize: '0.82rem', display: 'flex', justifyContent: 'space-between', gap: '10px', background: 'var(--bg-body)', padding: '6px 10px', borderRadius: '6px' }}>
                      <span>
                        <strong>{normalizarRol(int.rol)}:</strong>{' '}
                        {int.es_razon_social ? int.nombres : [int.apellido_paterno, int.apellido_materno, int.nombres].filter(Boolean).join(' ')}
                      </span>
                      {int.rut && <span style={{ color: 'var(--text-muted)' }}>{int.rut}</span>}
                    </div>
                  ))}
                </div>
              ) : (
                <span style={{ fontSize: '0.82rem', fontStyle: 'italic', color: 'var(--text-muted)' }}>Sin intervinientes registrados.</span>
              )}
            </div>

            {/* Metadata */}
            <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '1rem', fontSize: '0.75rem', color: 'var(--text-muted)', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {detalleItem.solicitud && <div>Vinculado a Solicitud/Trámite #{detalleItem.solicitud.id} ({detalleItem.solicitud.tipo_tramite})</div>}
              {detalleItem.usuario_responsable && <div>Tomado por: {detalleItem.usuario_responsable.name}</div>}
              {detalleItem.created_at && <div>Creado: {new Date(detalleItem.created_at).toLocaleString('es-CL')}</div>}
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Consulta de Sociedades (desde el formulario) ── */}
      {modalSociedades && (
        <div
          onClick={() => setModalSociedades(false)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(15, 23, 42, 0.45)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem'
          }}
        >
          <div
            className="glass-card animate-scale-in"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '720px', width: '100%', maxHeight: '85vh', overflowY: 'auto',
              padding: '2rem', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.15)',
              border: '1px solid var(--border-color)', background: 'var(--bg-surface)'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.2rem' }}>
              <div>
                <h3 style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--primary-dark)', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Building2 size={20} /> Historial Registral de Sociedad
                </h3>
                <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: '4px 0 0' }}>
                  Consulta los antecedentes mercantiles para vincular la inscripción anterior.
                </p>
              </div>
              <button type="button" className="icon-btn" onClick={() => setModalSociedades(false)} style={{ padding: '6px', border: '1px solid var(--border-color)', borderRadius: '8px' }}>
                <X size={16} />
              </button>
            </div>

            {/* Búsqueda dentro del modal */}
            <form 
              onSubmit={(e) => {
                e.preventDefault();
                consultarSociedad();
              }}
              style={{ display: 'flex', gap: '8px', marginBottom: '1rem' }}
            >
              <input
                type="text"
                placeholder="Razón Social..."
                value={busquedaSociedad}
                onChange={(e) => setBusquedaSociedad(e.target.value)}
                style={{ flex: 1, padding: '8px 12px', fontSize: '0.88rem' }}
              />
              <button
                type="submit"
                disabled={cargandoSociedad}
                className="btn btn-primary btn-sm"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '8px 14px' }}
              >
                {cargandoSociedad ? <RefreshCw className="spinner" size={14} /> : <Search size={14} />}
                <span>Buscar</span>
              </button>
            </form>

            {/* Filtros dinámicos */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '1rem' }}>
              {TIPOS_CONTRATO.map((tipo) => {
                const activo = filtroContratoSociedad === tipo;
                return (
                  <button
                    key={tipo}
                    type="button"
                    onClick={() => {
                      setFiltroContratoSociedad(tipo);
                      if (busquedaSociedad.trim()) consultarSociedad(busquedaSociedad, tipo);
                    }}
                    style={{
                      padding: '2px 8px',
                      borderRadius: '10px',
                      border: activo ? '1px solid var(--primary)' : '1px solid var(--border-color)',
                      background: activo ? 'var(--primary)' : 'transparent',
                      color: activo ? 'white' : 'var(--text-muted)',
                      fontSize: '0.72rem',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    {tipo}
                  </button>
                );
              })}
            </div>

            {/* Lista de resultados en el modal */}
            {cargandoSociedad ? (
              <div style={{ textAlign: 'center', padding: '2rem 0', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                <RefreshCw className="spinner" size={18} style={{ margin: '0 auto 6px' }} />
                <div>Buscando asientos de comercio...</div>
              </div>
            ) : inscripcionesSociedad.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem 1rem', background: 'var(--bg-body)', borderRadius: '8px' }}>
                <AlertCircle size={22} style={{ color: 'var(--warning)', margin: '0 auto 6px' }} />
                <p style={{ margin: 0, fontSize: '0.82rem', fontWeight: 600 }}>
                  {haBuscadoSociedad ? `Sin registros para "${busquedaSociedad}"` : 'Escribe el nombre y presiona Buscar'}
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '340px', overflowY: 'auto' }}>
                {inscripcionesSociedad.map((item) => {
                  const badge = obtenerBadgeContrato(item);
                  return (
                    <div
                      key={item.id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '10px 12px',
                        borderRadius: '8px',
                        border: '1px solid var(--border-color)',
                        background: 'var(--bg-body)'
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
                          <span style={{
                            padding: '2px 6px',
                            borderRadius: '6px',
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            background: badge.bg,
                            color: badge.text,
                            border: `1px solid ${badge.border}`
                          }}>
                            {badge.icon} {badge.label}
                          </span>
                          <strong style={{ fontSize: '0.82rem', color: 'var(--primary-dark)' }}>
                            Fs. {item.fojas} · N° {item.numero} · Año {item.ano}
                          </strong>
                        </div>
                        <div style={{ fontSize: '0.82rem', color: 'var(--text-main)', fontWeight: 700 }}>
                          {item.nombre_de_la_sociedad}
                        </div>
                        {item.intervinientes && item.intervinientes.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
                            {item.intervinientes.map((int, iIdx) => (
                              <span
                                key={int.id || iIdx}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '3px',
                                  background: 'var(--bg-surface)',
                                  border: '1px solid var(--border-color)',
                                  padding: '1px 6px',
                                  borderRadius: '4px',
                                  fontSize: '0.7rem',
                                  color: 'var(--text-main)'
                                }}
                              >
                                <strong style={{ color: 'var(--primary)', textTransform: 'uppercase', fontSize: '0.64rem' }}>
                                  {normalizarRol(int.rol)}:
                                </strong>{' '}
                                <span>{int.nombre_completo}</span>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          onClick={() => handleCargarSociedadEnFormulario(item, true)}
                          style={{ padding: '5px 10px', fontSize: '0.72rem', fontWeight: 600 }}
                        >
                          Vincular Anterior ↵
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default IngresoIndice;
